import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatPaise, toPaise, visibleStatuses } from '@pharmacia/core'
import type { BillStatus as BillStatusValue } from '@pharmacia/core'
import { ApiError, del } from '../lib/api.ts'
import { useAuth } from '../lib/auth.tsx'
import { useList } from '../lib/useList.ts'
import { formatDateTime } from '../lib/format.ts'
import { PageHeader } from '../components/AppShell.tsx'
import { Button, Empty, Input, Select, SkeletonRows } from '../components/ui.tsx'
import { IconCheck, IconEye, IconSearch, IconTrash } from '../components/icons.tsx'
import { ConfirmDialog } from '../components/ConfirmDialog.tsx'
import { CancelBillDialog } from '../components/CancelBillDialog.tsx'
import { BillStatus } from '../components/BillStatus.tsx'
import { useToast } from '../components/Toast.tsx'

type Bill = {
  id: string; number: string | null; status: 'draft' | 'final' | 'cancelled'
  total: string | null; issuedAt: string | null; createdAt: string
  paidAt: string | null
  version: number
  /** A draft's total is computed, not issued — shown as an estimate. */
  estimated: boolean
  prescriptionNumber: string
  patient: { id: string; mrn: string; name: string }
}

export function BillingPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const { can } = useAuth()

  // Voiding a bill, and seeing one that has been voided, are the same
  // permission. The API enforces both independently -- this only decides
  // what to draw.
  const mayVoid = can('billing:void')
  const [status, setStatus] = useState<BillStatusValue | ''>('')
  const list = useList<Bill>('/api/bills', status ? { status } : {})

  // Removing a bill is two different operations wearing one button.
  // A draft has no number and nothing was issued, so it is simply
  // discarded. An issued bill is a GST invoice from an unbroken series and
  // can only be cancelled, with a reason, which the database enforces
  // regardless of what this screen offers.
  const [removing, setRemoving] = useState<Bill | null>(null)

  return (
    <>
      <PageHeader title="Billing" />
      <div className="content">
        <div className="page-max stack" style={{ gap: 'var(--s-5)' }}>

          <div className="toolbar">
            <div className="search">
              <IconSearch className="ic" />
              <Input placeholder="Search by patient, MRN or bill number"
                value={list.q} onChange={e => list.setQ(e.target.value)}
                aria-label="Search bills" />
            </div>
            <Select value={status} aria-label="Filter by status"
              style={{ maxWidth: 190 }}
              onChange={e => setStatus(e.target.value as BillStatusValue | '')}>
              <option value="">All bills</option>
              {visibleStatuses(mayVoid).map(s => (
                <option key={s} value={s}>
                  {s === 'draft' ? 'Drafts' : s === 'final' ? 'Issued' : 'Cancelled'}
                </option>
              ))}
            </Select>
          </div>

          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead><tr>
                  <th>Bill</th><th>Patient</th><th>Prescription</th>
                  <th>Status</th><th className="money">Total</th><th>Date</th><th />
                </tr></thead>
                <tbody>
                  {list.loading ? <SkeletonRows rows={5} cols={7} /> : null}
                  {!list.loading && list.items.map(b => (
                    <tr key={b.id}>
                      <td className="mono">{b.number ?? <span className="dim">— draft —</span>}</td>
                      <td>
                        <div style={{ fontWeight: 550 }}>{b.patient.name}</div>
                        <div className="dim mono" style={{ fontSize: 'var(--text-xs)' }}>{b.patient.mrn}</div>
                      </td>
                      <td className="muted mono" style={{ fontSize: 'var(--text-xs)' }}>{b.prescriptionNumber}</td>
                      <td>
                        <BillStatus status={b.status} paidAt={b.paidAt} />
                      </td>
                      <td className="money" style={{ fontWeight: 650 }}>
                        {b.total ? (
                          <>
                            {formatPaise(toPaise(b.total))}
                            {b.estimated ? (
                              <div className="dim" style={{ fontSize: 'var(--text-xs)', fontWeight: 400 }}>
                                not yet issued
                              </div>
                            ) : null}
                          </>
                        ) : <span className="dim">—</span>}
                      </td>
                      <td className="dim">{formatDateTime(b.issuedAt ?? b.createdAt)}</td>
                      <td className="actions">
                        <span className="row-actions">
                          {b.status === 'draft' ? (
                            <Button size="sm" variant="primary" style={{ marginRight: 6 }}
                              onClick={() => navigate(`/billing/${b.id}`)}>
                              Issue bill
                            </Button>
                          ) : null}
                          {b.status === 'final' && !b.paidAt ? (
                            <Button size="sm" variant="primary" style={{ marginRight: 6 }}
                              onClick={() => navigate(`/billing/${b.id}?pay=1`)}>
                              <IconCheck size={14} /> Mark paid
                            </Button>
                          ) : null}
                          <button className="icon-btn" title="Open" aria-label="Open bill"
                            onClick={() => navigate(`/billing/${b.id}`)}><IconEye /></button>
                          {mayVoid && b.status !== 'cancelled' ? (
                            <button className="icon-btn danger"
                              title={b.status === 'draft' ? 'Discard this draft' : 'Cancel this bill'}
                              aria-label={b.status === 'draft'
                                ? `Discard draft for ${b.patient.name}`
                                : `Cancel bill ${b.number ?? ''}`}
                              onClick={() => setRemoving(b)}>
                              <IconTrash />
                            </button>
                          ) : null}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!list.loading && list.items.length === 0 ? (
              <Empty title={list.q ? 'No matches' : 'No bills yet'}>
                {list.q ? 'Try a different name or number.'
                  : 'A bill appears here when a doctor sends a prescription to billing.'}
              </Empty>
            ) : null}
          </div>

          {list.hasMore ? (
            <div className="row" style={{ justifyContent: 'center' }}>
              <Button onClick={list.loadMore} loading={list.loadingMore}>Load more</Button>
            </div>
          ) : null}
        </div>
      </div>

      {removing && removing.status === 'draft' ? (
        <ConfirmDialog
          title="Discard this draft?"
          body={`Nothing has been issued for ${removing.patient.name}, so nothing is lost. `
            + 'The prescription returns to the billing queue and can be billed again.'}
          confirmLabel="Discard" danger
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            try {
              await del(`/api/bills/${removing.id}`)
              toast.ok('Draft discarded')
              list.refresh()
            } catch (e) { toast.fail('Could not discard', (e as ApiError).message) }
          }}
        />
      ) : null}

      {removing && removing.status === 'final' ? (
        <CancelBillDialog
          billId={removing.id} version={removing.version} paid={Boolean(removing.paidAt)}
          onClose={() => setRemoving(null)}
          onDone={() => { setRemoving(null); toast.ok('Bill cancelled'); list.refresh() }}
        />
      ) : null}
    </>
  )
}
